import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import "./Css/FeedbackManagement.css";

const BASE_URL =
  "https://mondelezbc.vsmglobaltechnologies.com/api/incident-audio";

function FeedbackManagement() {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filterReview, setFilterReview] = useState("all");
  const [sortOrder, setSortOrder] = useState("latest");
  const [selectedReport, setSelectedReport] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const res = await fetch(BASE_URL);
      if (!res.ok) throw new Error("Failed to fetch feedbacks");
      const data = await res.json();
      setFeedbacks(data?.data || []);
    } catch (error) {
      console.error("Error:", error);
      alert("Failed to load feedbacks!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const updateReviewStatus = async (id) => {
    try {
      const res = await fetch(`${BASE_URL}/review/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: 1 }),
      });

      if (!res.ok) throw new Error("Failed to update");

      setFeedbacks((prev) =>
        prev.map((fb) => (fb._id === id ? { ...fb, isReviwed: 1 } : fb))
      );

      alert("Marked as reviewed!");
    } catch (err) {
      console.error(err);
      alert("Failed to mark as reviewed!");
    }
  };

  const filtered = feedbacks.filter((fb) => {
    if (filterReview === "yes") return fb.isReviwed;
    if (filterReview === "notYet") return !fb.isReviwed;
    return true;
  });

  const sorted = filtered.sort((a, b) => {
    if (sortOrder === "latest")
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    return new Date(a.updatedAt) - new Date(b.updatedAt);
  });

  const filteredByDate = sorted.filter((fb) => {
    const fbDate = new Date(fb.updatedAt);
    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    return (!from || fbDate >= from) && (!to || fbDate <= to);
  });

  const handleExport = () => {
    if (filteredByDate.length === 0) {
      alert("No data for selected date range!");
      return;
    }

    const exportData = filteredByDate.map((fb) => ({
      "Employee ID": fb.userId,
      "Employee Name": fb.name || fb.userName,
      Incident: fb.incidentName || fb.incidentId,
      Choice: fb.choice,
      Report: fb.message,
      Reviewed: fb.isReviwed ? "Yes" : "Not Yet",
      Date: new Date(fb.updatedAt).toLocaleString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Feedbacks");

    XLSX.writeFile(workbook, "Feedbacks.xlsx");
  };

  return (
    <div className="feedback-page">
      <div className="feedback-toolbar mb-3">
        <h3 className="m-0">Feedback Management</h3>

        <div className="toolbar-controls">
          <select
            className="form-select form-select-sm"
            value={filterReview}
            onChange={(e) => setFilterReview(e.target.value)}
          >
            <option value="all">All Reviews</option>
            <option value="yes">Reviewed</option>
            <option value="notYet">Not Yet Reviewed</option>
          </select>

          <select
            className="form-select form-select-sm"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="latest">Latest</option>
            <option value="oldest">Oldest</option>
          </select>

          <input
            type="date"
            className="form-control form-control-sm"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />

          <input
            type="date"
            className="form-control form-control-sm"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />

          <button
            className="btn btn-success btn-sm px-3"
            onClick={handleExport}
          >
            📊 Export
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead>
            <tr>
              <th>Employee ID</th>
              <th>Name</th>
              <th>Incident</th>
              <th>Choice</th>
              <th>Report</th>
              <th>Reviewed?</th>
              <th>Action</th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="text-center">
                  Loading...
                </td>
              </tr>
            ) : filteredByDate.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center text-muted">
                  No Feedback
                </td>
              </tr>
            ) : (
              filteredByDate.map((fb) => (
                <tr key={fb._id}>
                  <td>{fb.userId}</td>
                  <td>{fb.name || fb.userName}</td>
                  <td>{fb.incidentName || fb.incidentId}</td>
                  <td>{fb.choice}</td>

                  {/* View Report Button */}
                  <td>
                    {fb.message ? (
                      <button
                        className="btn btn-link p-0 text-primary"
                        onClick={() => setSelectedReport(fb.message)}
                      >
                        View Report
                      </button>
                    ) : (
                      <span className="text-muted">No Report</span>
                    )}
                  </td>

                  {/* Reviewed Column */}
                  <td>
                    <span
                      className={`badge ${
                        fb.isReviwed ? "bg-success" : "bg-warning"
                      }`}
                    >
                      {fb.isReviwed ? "Yes" : "Not Yet"}
                    </span>
                  </td>

                  {/* Tick Action */}
                  <td>
                    <button
                      className="btn btn-sm btn-success"
                      disabled={fb.isReviwed}
                      onClick={() => updateReviewStatus(fb._id)}
                    >
                      ✔
                    </button>
                  </td>

                  <td>{new Date(fb.updatedAt).toLocaleDateString()}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Report Modal */}
      {selectedReport && (
        <div
          className="feedback-modal-backdrop"
          onClick={() => setSelectedReport(null)}
        >
          <div
            className="feedback-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="feedback-modal-header">
              <h5>Report Details</h5>
              <button
                className="btn-close"
                onClick={() => setSelectedReport(null)}
              ></button>
            </div>
            <div className="feedback-modal-body">
              <p>{selectedReport}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackManagement;
